// hooks/useAdminManagementLogic.js
import { useState, useEffect } from 'react';
import adminApi from 'services/api/adminApi';

const useAdminManagementLogic = (isSuperAdmin) => {
  const [isNavCollapsed, setIsNavCollapsed] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalState, setModalState] = useState({
    isOpen: false,
    admin: null,
    mode: "add",
  });
  const [notification, setNotification] = useState(null);
  const [selectedAdmins, setSelectedAdmins] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role_id: "",
    is_active: true,
    is_super_admin: false,
  });
  const [errors, setErrors] = useState({});

  // Notification functions
  const closeNotification = () => {
    setNotification(null);
  };

  const showNotification = (message, type = "info") => {
    setNotification({ message, type });
  };

  // Data loading functions
  const loadRoles = async () => {
    try {
      const rolesData = await adminApi.getRoles();
      setRoles(rolesData);
    } catch (error) {
      console.error("Error fetching roles:", error);
      showNotification("Failed to fetch roles", "error");
    }
  };

  const loadAdmins = async () => {
    try {
      setLoading(true);
      const adminsData = await adminApi.getAdmins();
      setAdmins(adminsData);
    } catch (error) {
      console.error("Error fetching admins:", error);
      showNotification("Failed to fetch admins", "error");
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    if (isSuperAdmin) {
      loadRoles();
      loadAdmins();
    }
  }, [isSuperAdmin]);

  // Form handling functions
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const validationErrors = {};
    if (!formData.name.trim()) validationErrors.name = "Admin name is required";
    if (!formData.email.trim()) validationErrors.email = "Email is required";
    if (!formData.role_id) validationErrors.role_id = "Role is required";
    if (modalState.mode === "add" && !formData.password) validationErrors.password = "Password is required";
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showNotification("Please fix errors before saving", "error");
      return;
    }

    try {
      if (modalState.mode === "add") {
        await adminApi.createAdmin(formData);
        showNotification("Admin created successfully", "success");
      } else {
        await adminApi.updateAdmin(modalState.admin.id, formData);
        showNotification("Admin updated successfully", "success");
      }
      setModalState({ isOpen: false, admin: null, mode: "add" });
      loadAdmins();
    } catch (error) {
      console.error("Error saving admin:", error);
      const errorMessage = error.response?.data?.detail || "Failed to save admin";
      showNotification(errorMessage, "error");
    }
  };

  const handleAddAdmin = () => {
    setFormData({
      name: "",
      email: "",
      password: "",
      role_id: "",
      is_active: true,
      is_super_admin: false,
    });
    setErrors({});
    setModalState({ isOpen: true, admin: null, mode: "add" });
  };

  const handleEditAdmin = (admin) => {
    setFormData({
      name: admin.name,
      email: admin.email,
      password: "", // Don't pre-fill password
      role_id: admin.role?.id || "",
      is_active: admin.is_active,
      is_super_admin: admin.is_super_admin,
    });
    setErrors({});
    setModalState({ isOpen: true, admin, mode: "edit" });
  };

  // Admin management functions
  const handleDeleteAdmin = async (admin) => {
    if (window.confirm(`Are you sure you want to delete "${admin.name}"?`)) {
      try {
        await adminApi.deleteAdmin(admin.id);
        showNotification("Admin deleted successfully", "success");
        loadAdmins();
      } catch (error) {
        console.error("Error deleting admin:", error);
        const errorMessage = error.response?.data?.detail || "Failed to delete admin";
        showNotification(errorMessage, "error");
      }
    }
  };

  const handleToggleStatus = async (admin) => {
    try {
      await adminApi.updateAdmin(admin.id, {
        is_active: !admin.is_active,
      });
      showNotification(
        `Admin ${admin.is_active ? "deactivated" : "activated"} successfully`,
        "success"
      );
      loadAdmins();
    } catch (error) {
      console.error("Error toggling admin status:", error);
      const errorMessage = error.response?.data?.detail || "Failed to update admin status";
      showNotification(errorMessage, "error");
    }
  };

  const handleToggleSuperAdmin = async (admin) => {
    try {
      await adminApi.updateAdmin(admin.id, {
        is_super_admin: !admin.is_super_admin,
      });
      showNotification(
        `Admin ${admin.is_super_admin ? "removed from" : "granted"} super admin privileges`,
        "success"
      );
      loadAdmins();
    } catch (error) {
      console.error("Error toggling super admin status:", error);
      const errorMessage = error.response?.data?.detail || "Failed to update admin status";
      showNotification(errorMessage, "error");
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedAdmins.length === 0) {
      showNotification("Please select at least one admin", "error");
      return;
    }

    try {
      switch (action) {
        case "activate":
          await adminApi.bulkActivateAdmins(selectedAdmins);
          showNotification("Admins activated successfully", "success");
          break;
        case "deactivate":
          await adminApi.bulkDeactivateAdmins(selectedAdmins);
          showNotification("Admins deactivated successfully", "success");
          break;
        case "delete":
          if (window.confirm(`Are you sure you want to delete ${selectedAdmins.length} admins?`)) {
            await adminApi.bulkDeleteAdmins(selectedAdmins);
            showNotification("Admins deleted successfully", "success");
          }
          break;
        default:
          break;
      }
      setSelectedAdmins([]);
      setShowBulkActions(false);
      loadAdmins();
    } catch (error) {
      console.error(`Error in bulk ${action}:`, error);
      const errorMessage = error.response?.data?.detail || `Failed to ${action} admins`;
      showNotification(errorMessage, "error");
    }
  };

  const handleExportCSV = async () => {
    try {
      const blob = await adminApi.exportAdminsCSV();
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `admins_${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showNotification("Admins exported successfully", "success");
    } catch (error) {
      console.error("Error exporting admins:", error);
      showNotification("Failed to export admins", "error");
    }
  };

  // Selection handling functions
  const handleSelectAdmin = (adminId) => {
    if (selectedAdmins.includes(adminId)) {
      setSelectedAdmins(selectedAdmins.filter((id) => id !== adminId));
    } else {
      setSelectedAdmins([...selectedAdmins, adminId]);
    }
  };

  const handleSelectAllAdmins = () => {
    if (selectedAdmins.length === admins.length) {
      setSelectedAdmins([]);
    } else {
      setSelectedAdmins(admins.map((admin) => admin.id));
    }
  };

  // Table columns definition
  const columns = [
    {
      header: (
        <input
          type="checkbox"
          checked={selectedAdmins.length === admins.length && admins.length > 0}
          onChange={handleSelectAllAdmins}
          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      ),
      accessor: "select",
      cell: (row) => (
        <input
          type="checkbox"
          checked={selectedAdmins.includes(row.id)}
          onChange={() => handleSelectAdmin(row.id)}
          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      ),
    },
    {
      header: "Name",
      accessor: "name",
      cell: (row) => (
        <div className="flex items-center">
          <span className="font-medium">{row.name}</span>
          {row.is_super_admin && (
            <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
              Super Admin
            </span>
          )}
        </div>
      ),
    },
    {
      header: "Email",
      accessor: "email",
      cell: (row) => <span>{row.email}</span>,
    },
    {
      header: "Role",
      accessor: "role",
      cell: (row) => (
        <span>{row.role ? row.role.name : "No role"}</span>
      ),
    },
    {
      header: "Status",
      accessor: "is_active",
      cell: (row) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          row.is_active 
            ? "bg-green-100 text-green-800" 
            : "bg-red-100 text-red-800"
        }`}>
          {row.is_active ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      header: "Created At",
      accessor: "created_at",
      cell: (row) => (
        <span>
          {row.created_at ? new Date(row.created_at).toLocaleDateString() : "N/A"}
        </span>
      ),
    },
    {
      header: "Actions",
      accessor: "actions",
      cell: (row) => (
        <div className="flex space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEditAdmin(row)}
            className="p-1.5"
          >
            <Icon name="Edit" size={16} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleToggleStatus(row)}
            className={`p-1.5 ${row.is_active ? "text-yellow-500 hover:text-yellow-700" : "text-green-500 hover:text-green-700"}`}
          >
            <Icon name={row.is_active ? "UserX" : "UserCheck"} size={16} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleToggleSuperAdmin(row)}
            className={`p-1.5 ${row.is_super_admin ? "text-yellow-500 hover:text-yellow-700" : "text-purple-500 hover:text-purple-700"}`}
          >
            <Icon name={row.is_super_admin ? "ShieldOff" : "Shield"} size={16} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDeleteAdmin(row)}
            className="p-1.5 text-red-500 hover:text-red-700"
          >
            <Icon name="Trash" size={16} />
          </Button>
        </div>
      ),
    },
  ];

  return {
    // State
    isNavCollapsed,
    admins,
    roles,
    loading,
    modalState,
    notification,
    selectedAdmins,
    showBulkActions,
    formData,
    errors,
    columns,
    
    // State setters
    setIsNavCollapsed,
    setModalState,
    setSelectedAdmins,
    setShowBulkActions,
    
    // Functions
    closeNotification,
    showNotification,
    loadAdmins,
    handleInputChange,
    validateForm,
    handleSave,
    handleAddAdmin,
    handleEditAdmin,
    handleDeleteAdmin,
    handleToggleStatus,
    handleToggleSuperAdmin,
    handleBulkAction,
    handleExportCSV,
    handleSelectAdmin,
    handleSelectAllAdmins
  };
};

export default useAdminManagementLogic;